import React, { createContext, useState, useCallback, useRef } from 'react';
import notificationService from '../services/notificationService';
import communicationService, { isNotificationEmailSyncEnabled } from '../services/communicationService';

export const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [communicationPrefs, setCommunicationPrefs] = useState({
    email_notifications_enabled: false,
    loaded: false,
  });
  const [prefsSaving, setPrefsSaving] = useState(false);
  const emailedIdsRef = useRef(new Set());

  const loadCommunicationPreferences = useCallback(async () => {
    try {
      const data = await communicationService.getPreferences();
      setCommunicationPrefs({
        email_notifications_enabled: Boolean(data?.email_notifications_enabled),
        loaded: true,
      });
    } catch {
      setCommunicationPrefs((prev) => ({ ...prev, loaded: true }));
    }
  }, []);

  const updateCommunicationPreferences = useCallback(async (patch) => {
    setPrefsSaving(true);
    try {
      const data = await communicationService.updatePreferences(patch);
      setCommunicationPrefs({
        email_notifications_enabled: Boolean(data?.email_notifications_enabled),
        loaded: true,
      });
      return data;
    } finally {
      setPrefsSaving(false);
    }
  }, []);

  const syncNotificationEmails = useCallback(async (items, emailEnabled) => {
    if (!isNotificationEmailSyncEnabled || !emailEnabled) return;
    const pending = items.filter((n) => !n.is_read && !emailedIdsRef.current.has(n.id));
    await Promise.all(
      pending.map(async (n) => {
        try {
          await communicationService.sendNotificationEmail(n.id);
          emailedIdsRef.current.add(n.id);
        } catch {
          // If backend endpoint is not configured, keep UI stable and skip retries until next refresh.
        }
      })
    );
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const [data, prefs] = await Promise.all([
        notificationService.getAll(),
        communicationPrefs.loaded
          ? Promise.resolve(communicationPrefs)
          : communicationService.getPreferences().then((payload) => ({
              email_notifications_enabled: Boolean(payload?.email_notifications_enabled),
              loaded: true,
            })).catch(() => ({ email_notifications_enabled: false, loaded: true })),
      ]);

      if (!communicationPrefs.loaded) {
        setCommunicationPrefs(prefs);
      }

      setNotifications(data);
      setUnreadCount(data.filter(n => !n.is_read).length);
      await syncNotificationEmails(data, prefs.email_notifications_enabled);
    } catch {}
  }, [communicationPrefs, syncNotificationEmails]);

  const markRead = useCallback(async (id) => {
    try {
      await notificationService.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await notificationService.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loadNotifications,
        markRead,
        markAllRead,
        communicationPrefs,
        prefsSaving,
        loadCommunicationPreferences,
        updateCommunicationPreferences,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}